import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { documentsApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { Upload as UploadIcon, Loader2, FileText } from "lucide-react";

export default function UploadDocument() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      if (!isMaster(user)) setBizId(userBusinessIds(user)[0] || b[0]?.id || "");
    });
  }, [user]);

  const pick = (f) => {
    setFile(f);
    if (f && f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const [addedCount, setAddedCount] = useState(0);
  const submit = async (e, addAnother = false) => {
    e.preventDefault();
    if (!file || !bizId) return toast.error("Choose a business and a file");
    setUploading(addAnother ? "another" : "process");
    try {
      const fd = new FormData();
      fd.append("business_id", bizId);
      fd.append("file", file);
      const res = await documentsApi.upload(fd);
      if (addAnother) {
        // Keep the business; clear the file so the next one can be dropped in.
        toast.success(`"${file.name}" submitted — AI extraction running in the background`);
        setAddedCount((n) => n + 1);
        pick(null);
        if (inputRef.current) inputRef.current.value = "";
      } else {
        toast.success("Uploaded — AI extraction started");
        nav(`/documents/${res.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-4xl font-bold text-espresso mb-2" data-testid="upload-title">Upload document</h1>
      <p className="text-espresso-ink/70 mb-8">Drop a receipt or invoice — DocHub reads it, tags VAT, and stages a draft bill.</p>

      <form onSubmit={submit} className="bg-white border border-canvas-border rounded-2xl p-8 shadow-soft space-y-6">
        {(isMaster(user) || businesses.length > 1) && (
          <div>
            <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Business</label>
            <select
              data-testid="upload-business"
              value={bizId}
              onChange={(e) => setBizId(e.target.value)}
              required
              className="w-full mt-1.5 px-4 py-3 bg-white border border-canvas-border rounded-lg brand-focus"
            >
              <option value="">Choose a business</option>
              {businesses.map((b) => <option key={b.id} value={b.id}>{b.name} · {b.accounting}</option>)}
            </select>
          </div>
        )}

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) pick(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-canvas-border rounded-2xl p-12 text-center cursor-pointer hover:border-gold hover:bg-gold-soft/30 transition-colors"
          data-testid="upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) => pick(e.target.files[0])}
            data-testid="upload-file-input"
          />
          {file ? (
            <div>
              <FileText className="w-10 h-10 text-gold mx-auto mb-3" />
              <div className="font-semibold text-espresso">{file.name}</div>
              <div className="text-xs text-espresso-ink/60 mt-1">{(file.size / 1024).toFixed(0)} KB</div>
              {preview && <img src={preview} alt="preview" className="mt-4 mx-auto max-h-48 rounded-lg" />}
            </div>
          ) : (
            <>
              <UploadIcon className="w-10 h-10 text-espresso-ink/40 mx-auto mb-3" />
              <div className="font-semibold text-espresso">Drop a receipt or PDF here</div>
              <div className="text-xs text-espresso-ink/60 mt-1">or click to browse (JPG, PNG, PDF)</div>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="upload-submit"
            type="submit"
            disabled={!file || !bizId || !!uploading}
            className="flex-1 py-3 bg-espresso text-canvas rounded-lg font-semibold hover:bg-espresso-light transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {uploading === "process" && <Loader2 className="w-4 h-4 animate-spin" />} Submit &amp; process
          </button>
          <button
            data-testid="upload-submit-another"
            type="button"
            onClick={(e) => submit(e, true)}
            disabled={!file || !bizId || !!uploading}
            className="flex-1 py-3 bg-white border-2 border-espresso text-espresso rounded-lg font-semibold hover:bg-gold-soft/40 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {uploading === "another" && <Loader2 className="w-4 h-4 animate-spin" />} Submit &amp; add another
          </button>
        </div>
        {addedCount > 0 && (
          <div className="text-sm text-espresso-ink/70 text-center" data-testid="upload-added-count">
            {addedCount} document{addedCount > 1 ? "s" : ""} submitted this session ·{" "}
            <button type="button" onClick={() => nav("/documents")} className="underline font-semibold text-espresso" data-testid="upload-go-inbox">Go to inbox</button>
          </div>
        )}
      </form>
    </div>
  );
}
